/**
 * Deterministic host election. Members are ranked by admission epoch (the
 * room creator is epoch 0; each admitted peer gets max+1), ties broken by
 * identity id. The host is the first live member in that order; when it goes
 * silent the next live one claims `term + 1`. Higher term always wins; at an
 * equal term the better-ranked claimant wins (split-brain resolution).
 */

export interface Member {
  id: string;
  name: string;
  /** base64 raw public key */
  pub: string;
  epoch: number;
  /** Small integer used on the binary player channel. */
  slot: number;
}

export function hostOrder(members: Iterable<Member>): Member[] {
  return [...members].sort((a, b) => a.epoch - b.epoch || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

export function rankOf(order: Member[], id: string): number {
  const i = order.findIndex((m) => m.id === id);
  return i < 0 ? Number.MAX_SAFE_INTEGER : i;
}

/** First member in order that is alive and not excluded. */
export function successor(order: Member[], alive: (id: string) => boolean, exclude: ReadonlySet<string> = new Set()): string | null {
  for (const m of order) if (!exclude.has(m.id) && alive(m.id)) return m.id;
  return null;
}

export interface Claim { term: number; id: string }

/** Does claim `a` beat claim `b`? */
export function claimBeats(a: Claim, b: Claim, order: Member[]): boolean {
  if (a.term !== b.term) return a.term > b.term;
  return rankOf(order, a.id) < rankOf(order, b.id);
}

/**
 * Should a peer currently following `current` (at its term) accept `claim`?
 * A claim at a higher term is accepted only if the claimant is the best live
 * candidate in our own view (or we have no live better candidate); this stops
 * a low-ranked peer with a bad link from grabbing the room.
 */
export function acceptClaim(
  claim: Claim, current: Claim, order: Member[], alive: (id: string) => boolean,
): boolean {
  if (claim.term < current.term) return false;
  if (claim.term === current.term) return claim.id === current.id || claimBeats(claim, current, order);
  for (const m of order) {
    if (m.id === claim.id) return true;
    if (m.id !== current.id && alive(m.id)) return false; // someone better is alive in our view
  }
  return false; // unknown claimant
}

export function nextEpoch(members: Iterable<Member>): number {
  let max = -1;
  for (const m of members) max = Math.max(max, m.epoch);
  return max + 1;
}

export function freeSlot(members: Iterable<Member>): number {
  const used = new Set<number>();
  for (const m of members) used.add(m.slot);
  let s = 0;
  while (used.has(s)) s++;
  return s;
}
